import { Link, Navigate, useParams } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { AlternateHead } from '@/i18n/AlternateHead'
import { chromeCopy } from '@/i18n/chrome'
import { href } from '@/i18n/locale'
import { useLocale } from '@/i18n/useLocale'
import { getResourceArticle, resourceArticlePath, RESOURCES } from '@/data/resources'
import { EditorialArticle, articleChrome, articleLang } from '@/components/EditorialArticle'
import { getArticleImageUrl } from '@/data/resourceImages'
import { AUTHOR_ID, ORGANIZATION_ID, SITE_URL } from '@/data/seoConstants'
import '@/styles/editorial.css'

const PILLAR = 'ai-automation-for-small-business'

export function ResourceArticle() {
  const { slug } = useParams()
  const locale = useLocale()
  const article = getResourceArticle(slug, locale)

  if (!article) return <Navigate to={href('/resources', locale)} replace />

  const lang = articleLang(article.language)
  const chrome = articleChrome[lang]
  const articlePath = resourceArticlePath(article)
  const url = `${SITE_URL}${articlePath}`
  const imageUrl = `${SITE_URL}${getArticleImageUrl(article.coverImage)}`
  const imageAlt = article.imageAlt ?? article.title
  const seoTitle = article.seoTitle ?? article.title
  const datePublished = article.datePublished ?? article.dateModified
  const cta = article.cta ?? {
    eyebrow: chrome.ctaEyebrow,
    heading: chrome.ctaHeading,
    label: chrome.ctaLabel,
    href: chrome.ctaHref,
  }

  /** Cluster pillars point at their own cluster; everything else falls back to the library pillar. */
  const pillarSlug = article.pillarSlug ?? PILLAR
  const pillar = getResourceArticle(pillarSlug, locale)
  const isOwnPillar = article.isPillar || article.isClusterPillar

  const related = article.relatedSlugs
    .map((item) => getResourceArticle(item, locale))
    .filter((item): item is (typeof RESOURCES)[number] => Boolean(item))

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      name: seoTitle,
      description: article.metaDescription,
      datePublished,
      dateModified: article.dateModified,
      inLanguage: article.language ?? 'en',
      author: article.author
        ? { '@type': 'Person', '@id': AUTHOR_ID, name: 'Mohamed Amine Saada' }
        : { '@id': ORGANIZATION_ID },
      publisher: { '@id': ORGANIZATION_ID },
      image: { '@type': 'ImageObject', url: imageUrl, width: 1200, height: 675, caption: imageAlt },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      isAccessibleForFree: true,
      ...(isOwnPillar ? {} : { isPartOf: { '@id': `${SITE_URL}${pillar ? resourceArticlePath(pillar) : href(`/resources/${pillarSlug}`, locale)}` } }),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: chromeCopy[locale].resources, item: `${SITE_URL}${href('/resources', locale)}` },
        { '@type': 'ListItem', position: 2, name: article.title, item: url },
      ],
    },
  ]

  const breadcrumb = [
    { label: chromeCopy[locale].resources, to: href('/resources', locale) },
    ...(article.cluster ? [{ label: article.cluster }] : []),
    { label: article.title },
  ]

  return (
    <main className="fw-editorial min-h-screen bg-fw-white">
      <AlternateHead
        title={`${seoTitle}, Forteuno Web`}
        description={article.metaDescription}
        canonicalPath={articlePath}
        ogType="article"
      />
      <Head>
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="675" />
        <meta property="og:image:alt" content={imageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Head>

      <EditorialArticle
        lang={lang}
        breadcrumb={breadcrumb}
        title={article.title}
        excerpt={article.excerpt}
        asideEyebrow={!isOwnPillar && pillar ? chrome.series : chrome.startHere}
        asideBody={
          !isOwnPillar && pillar ? (
            <>
              {chrome.partOf} <Link to={resourceArticlePath(pillar)}>{pillar.title}</Link>
              {chrome.seriesSuffix}
            </>
          ) : (
            chrome.startHereBlurb
          )
        }
        author={article.author}
        authorHref={article.author ? href('/about/mohamed-amine-saada', locale) : undefined}
        datePublished={datePublished}
        dateModified={article.dateModified}
        reviewStatus={article.reviewStatus}
        coverImage={article.coverImage}
        imageAlt={imageAlt}
        body={article.body}
        applyLinks={chrome.applyLinks}
        related={related.map((item) => ({
          href: resourceArticlePath(item),
          title: item.title,
          excerpt: item.excerpt,
        }))}
        cta={cta}
        resolveHref={(path) => href(path, locale)}
      />
    </main>
  )
}
